/** @format */
import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { motion } from "framer-motion";

const API_URL = process.env.REACT_APP_FRONTEND_URL;
const GET_STATUS_CHANGES = `${API_URL}/v1/hris/employeeStatusChange/status-changes`;
const POST_STATUS_CHANGE = `${API_URL}/v1/hris/employeeStatusChange/status-changes`;

const EmployeeTermination = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [keyword, setKeyword] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const token = Cookies.get("user_token");
                const res = await fetch(`${API_URL}/v1/hris/employees/getemployeebasicdetails`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                const result = await res.json();

                if (Array.isArray(result)) {
                    // map for react-select
                    const formatted = result.map((emp) => ({
                        value: emp.employee_no,
                        label: `${emp.employee_no} — ${emp.employee_fullname}`,
                        fullName: emp.employee_fullname,
                    }));
                    setEmployees(formatted);
                }
            } catch (err) {
                console.error("Error fetching employees:", err);
                toast.error("Failed to load employee list");
            }
        };
        fetchEmployees();
    }, []);


    // Drawer + Form
    const [showDrawer, setShowDrawer] = useState(false);
    const [formData, setFormData] = useState({
        employeeId: "",
        type: "resignation",
        date: "",
        reason: "",
        status_document: null,
    });

    const [showConfirm, setShowConfirm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // ===== Avatar Colors =====
    const avatarBgClass = (seed = "") => {
        const palette = [
            "bg-sky-500",
            "bg-indigo-500",
            "bg-emerald-500",
            "bg-rose-500",
            "bg-amber-500",
            "bg-teal-500",
            "bg-fuchsia-500",
            "bg-cyan-500",
        ];
        let hash = 0;
        for (let i = 0; i < seed.length; i++)
            hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
        return palette[hash % palette.length];
    };

    const getInitials = (fullName = "") => {
        const tokens = String(fullName)
            .replace(/[^\p{L}\p{N}\s'-]/gu, "")
            .trim()
            .split(/\s+/)
            .filter(Boolean);
        if (tokens.length === 0) return "??";
        if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
        return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
    };

    // ===== Fetch with filters + pagination =====
    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = Cookies.get("user_token");
            const params = new URLSearchParams({ page, limit });

            if (keyword) params.append("keyword", keyword);
            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);

            const res = await fetch(`${GET_STATUS_CHANGES}?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

            const result = await res.json();
            const list = Array.isArray(result?.data?.rows)
                ? result.data.rows
                : result.data || [];

            setData(list);
            setTotalPages(result?.data?.totalPages || 1);
        } catch (err) {
            setError(err.message || "Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, limit]);

    // ===== Form handlers =====
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData((prev) => ({ ...prev, status_document: file }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const token = Cookies.get("user_token");
            const method = editingId ? "PUT" : "POST";
            const url = editingId
                ? `${API_URL}/v1/hris/employeeStatusChange/status-changes/${editingId}`
                : POST_STATUS_CHANGE;

            const fd = new FormData();
            fd.append("employeeId", formData.employeeId);
            fd.append("type", formData.type);
            fd.append("date", formData.date);
            fd.append("reason", formData.reason);
            if (formData.status_document) {
                fd.append("status_document", formData.status_document);
            }

            const res = await fetch(url, {
                method,
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });

            const result = await res.json();

            if (res.ok && result?.success) {
                // ✅ Show API success message in toast
                toast.success(result.message || "Status change recorded successfully.");

                // Refresh table
                await fetchData();

                // Reset form and close drawer
                setShowDrawer(false);
                setEditingId(null);
                setFormData({
                    employeeId: "",
                    type: "resignation",
                    date: "",
                    reason: "",
                    status_document: null,
                });
            } else {
                // ❌ Show API error message
                toast.error(result.message || "Something went wrong while saving.");
            }
        } catch (err) {
            toast.error(err.message || "Unexpected error occurred.");
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.4 }}
        >

            <div className="font-montserrat">
                <p className="text-[25px] my-4">Employee Termination Details</p>

                {/* ===== Filters ===== */}
                <div className="bg-white shadow p-4 rounded-md mb-4 flex flex-wrap gap-4">
                    <input
                        type="text"
                        placeholder="Search Employee ID / Name"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="border rounded-md p-2 w-60"
                    />
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border rounded-md p-2"
                    />
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border rounded-md p-2"
                    />
                    <button
                        onClick={() => {
                            setPage(1);
                            fetchData();
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md"
                    >
                        Apply Filters
                    </button>
                    <button
                        onClick={() => {
                            setKeyword("");
                            setStartDate("");
                            setEndDate("");
                            setPage(1);
                            fetchData();
                        }}
                        className="px-4 py-2 bg-gray-300 rounded-md"
                    >
                        Reset
                    </button>
                </div>

                {/* ===== Table ===== */}
                <div className="overflow-x-auto bg-white shadow p-2 rounded-xl">
                    <div className="flex justify-end mb-4">
                        <button
                            onClick={() => setShowDrawer(true)}
                            className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600"
                        >
                            Change Employment
                        </button>
                    </div>

                    <table className="w-full text-sm text-left">
                        <thead className="bg-white text-gray-600 border-b border-t">
                            <tr>
                                <th className="px-6 py-4">Employee No</th>
                                <th className="px-6 py-4">Employee Name</th>
                                <th className="px-6 py-4">Type</th>
                                <th className="px-6 py-4">Reason</th>
                                <th className="px-6 py-4">Edited At</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 border-t border-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-gray-500">
                                        Loading...
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-red-500">
                                        Error: {error}
                                    </td>
                                </tr>
                            ) : data.length > 0 ? (
                                data.map((row) => (
                                    <tr key={row.id} className="border-b hover:bg-gray-50">
                                        <td className="p-3 text-blue-600">{row.employeeId}</td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs shrink-0 ${avatarBgClass(
                                                        row.employee_fullname || row.employeeId
                                                    )}`}
                                                    title={row.employee_fullname}
                                                >
                                                    {getInitials(row.employee_fullname || row.employeeId)}
                                                </div>
                                                <div>
                                                    <div className="font-semibold leading-5">
                                                        {row.employee_fullname}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        EMP ID: {row.employeeId}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 capitalize">{row.type}</td>
                                        <td className="p-3">{row.reason}</td>

                                        <td className="p-3">
                                            {new Date(row.updatedAt).toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-gray-500">
                                        No data found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="flex justify-between items-center px-4 py-2 text-sm">
                        <div>
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                            >
                                Next
                            </button>
                            <select
                                value={limit}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                    setPage(1);
                                }}
                                className="border rounded-md p-1"
                            >
                                {[5, 10, 20, 50].map((n) => (
                                    <option key={n} value={n}>
                                        {n} / page
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {showDrawer && (
                    <div className="fixed inset-0 flex justify-end z-50">
                        <div
                            className="absolute inset-0 bg-black bg-opacity-40"
                            onClick={() => setShowDrawer(false)}
                        ></div>

                        <div className="relative w-96 bg-white shadow-xl h-full p-6 animate-slideInRight">
                            <h2 className="text-xl font-semibold mb-4">Change Employee Status</h2>



                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Select Employee</label>
                                    <Select
                                        options={employees}
                                        value={selectedEmployee}
                                        onChange={(option) => {
                                            setSelectedEmployee(option);
                                            setFormData((prev) => ({
                                                ...prev,
                                                employeeId: option?.value || "",
                                            }));
                                        }}
                                        isClearable
                                        placeholder="Select an employee..."
                                        className="text-sm"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                borderColor: "#D1D5DB",
                                                borderRadius: "6px",
                                                minHeight: "42px",
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isFocused ? "#EFF6FF" : "white",
                                                color: "#111827",
                                            }),
                                        }}
                                    />
                                </div>


                                {/* 🔹 Status Type */}
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    className="w-full border rounded-md p-2"
                                    required
                                >
                                    <option value="resignation">Resignation</option>
                                    <option value="termination">Termination</option>
                                    <option value="contract end">Contract End</option>
                                </select>

                                {/* 🔹 Date */}
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    className="w-full border rounded-md p-2"
                                    required
                                />

                                {/* 🔹 Reason */}
                                <textarea
                                    name="reason"
                                    value={formData.reason}
                                    onChange={handleChange}
                                    className="w-full border rounded-md p-2"
                                    rows="3"
                                    placeholder="Reason"
                                    required
                                />

                                {/* 🔹 File Upload */}
                                <label className="block text-sm font-medium">
                                    Status Document (optional)
                                </label>
                                <div className="flex items-center justify-center w-full">
                                    <label className="flex flex-col w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                        <div className="flex flex-col items-center justify-center pt-7">
                                            <svg
                                                className="w-8 h-8 text-gray-400"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M7 16V4m0 0l-4 4m4-4l4 4M17 8v12m0 0l-4-4m4 4l4-4"
                                                />
                                            </svg>
                                            <p className="pt-1 text-sm tracking-wider text-gray-400">
                                                {formData.status_document
                                                    ? formData.status_document.name
                                                    : "Select a file"}
                                            </p>
                                        </div>
                                        <input
                                            type="file"
                                            className="opacity-0"
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                </div>

                                {/* 🔹 Actions */}
                                <div className="flex justify-end gap-2 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowDrawer(false)}
                                        className="px-4 py-2 bg-gray-300 rounded-md"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={submitting}
                                        onClick={() => setShowConfirm(true)}
                                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                    >
                                        {submitting ? "Saving..." : "Save"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}


                {/* ===== Confirmation Modal ===== */}
                {showConfirm && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white p-6 rounded shadow-md w-96">
                            <h3 className="text-lg font-semibold mb-4">
                                Confirm Status Change
                            </h3>
                            <p className="mb-6">
                                Are you sure you want to save these changes?
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    className="px-4 py-2 bg-gray-300 rounded"
                                    onClick={() => setShowConfirm(false)}
                                >
                                    No
                                </button>
                                <button
                                    className="px-4 py-2 bg-blue-500 text-white rounded"
                                    onClick={async () => {
                                        setShowConfirm(false);
                                        await handleSubmit();
                                    }}
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Animation */}
                <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    pauseOnHover
                    draggable
                    theme="colored"
                />

            </div>
        </motion.div>

    );
};

export default EmployeeTermination;
